import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';

import type { PersonaAvatarProfile } from '@repo/contracts';
import {
  ONBOARDING_ROLES,
  ONBOARDING_SOURCES,
  type OnboardingRole,
  type OnboardingSource,
} from '@repo/contracts/onboarding-options';
import { Button } from '@repo/ui/button';
import { Input } from '@repo/ui/input';

import { trackEvent } from '../analytics/posthog';
import { readUtm } from '../analytics/utm';
import { ApiError } from '../api/client';
import { ArrowRightIcon } from '../lib/icons';
import { PersonaAvatar } from '../persona/persona-avatar';
import { AuthLayout, ShowcasePanel } from './auth-layout';
import { ExtensionSignedIn } from './extension-signed-in';
import {
  completeOnboarding,
  detectCountry,
  onboardingKeys,
} from './onboarding-api';
import { safeRedirect, withSignInParams } from './redirect';
import { useAuth } from './use-auth';
import {
  useExtensionAuthHandoff,
  useIsExtensionAuthFlow,
} from './use-extension-auth-handoff';

/** flotoapps' placeholder name for an account with none — never prefilled. */
const PLACEHOLDER_NAME = 'Floto User';

/** Same height and shape as the sign-in screen's email field. */
const CONTROL =
  'h-[46px] rounded-button border border-hairline-alt bg-surface px-3.5 text-ui-lg text-text-primary';

/** The account's own name, unless it is flotoapps' placeholder. */
function nameOf(displayName: string | null | undefined): string {
  const name = displayName?.trim() ?? '';
  return name === PLACEHOLDER_NAME ? '' : name;
}

/** One field on screen, two on the profile: the first word is the first name,
 *  the rest the last — "Mary Ann Smith" keeps "Ann Smith" together. */
function splitName(fullName: string) {
  const [first = '', ...rest] = fullName.trim().split(/\s+/);
  return { first, last: rest.join(' ') };
}

/**
 * Four panel members saying hello on the showcase side — the people this
 * form is introducing the user to. Real portraits from the panel's own
 * DiceBear style, the appearance fixed so the scene is the same every time.
 */
const GREETERS: {
  delay: string;
  hi: string;
  name: string;
  place: string;
  profile: PersonaAvatarProfile;
}[] = [
  {
    delay: '[animation-delay:-1s]',
    hi: '-top-6 -left-3 -rotate-6',
    name: 'Arjun',
    place: 'top-[50%] left-[9%]',
    profile: {
      clothes: 'squared',
      clothingColor: '456dff',
      eyes: 'open',
      hair: 'shortCombover',
      hairColor: '6c4545',
      mouth: 'smile',
      nose: 'mediumRound',
      skinColor: 'e5a07e',
    },
  },
  {
    delay: '[animation-delay:-3s]',
    hi: '-top-6 -right-2 rotate-6',
    name: 'Hana',
    place: 'top-[44%] right-[9%]',
    profile: {
      clothes: 'rounded',
      clothingColor: '54d7c7',
      eyes: 'happy',
      hair: 'cap',
      hairColor: 'e15c66',
      mouth: 'bigSmile',
      nose: 'smallRound',
      skinColor: 'eeb4a4',
    },
  },
  {
    delay: '[animation-delay:-5s]',
    hi: '-top-6 left-2 -rotate-3',
    name: 'Kofi',
    place: 'top-[64%] left-[50%]',
    profile: {
      clothes: 'checkered',
      clothingColor: '6dbb58',
      eyes: 'open',
      facialHair: 'beardMustache',
      hair: 'fade',
      hairColor: '362c47',
      mouth: 'smile',
      nose: 'wrinkles',
      skinColor: 'd78774',
    },
  },
  {
    delay: '[animation-delay:-2s]',
    hi: '-top-5 -left-4 -rotate-6',
    name: 'Ava',
    place: 'top-[69%] right-[9%]',
    profile: {
      clothes: 'rounded',
      clothingColor: 'f55d81',
      eyes: 'happy',
      hair: 'long',
      hairColor: '362c47',
      mouth: 'smile',
      nose: 'smallRound',
      skinColor: '92594b',
    },
  },
];

/**
 * "/login/onboarding" — the one-screen signup form a new user fills in after
 * their first sign-in, ported from flotoapps' `/onboarding` (same fields, same
 * options, same copy) so a Floto profile is the same whichever product it
 * started in. The API registered them with the jo service at sign-in; this
 * form activates them.
 *
 * Reached from ProtectedRoute and both sign-in paths whenever the session says
 * `activated: false`. Lives under /login so an extension sign-in can still be
 * handed its session from here once the form is done — see onboardingPathFor.
 */
export function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const redirect = safeRedirect(searchParams.get('redirect'));
  const { activated, loading, refreshSession, signOut, user } = useAuth();
  const isExtensionFlow = useIsExtensionAuthFlow();
  const handOffToExtension = useExtensionAuthHandoff();
  const finished = useRef(false);

  const [fullName, setFullName] = useState<string | null>(null);
  const [role, setRole] = useState<OnboardingRole | ''>('');
  const [company, setCompany] = useState('');
  const [country, setCountry] = useState<string | null>(null);
  const [source, setSource] = useState<OnboardingSource | ''>('');
  const [formError, setFormError] = useState<string | null>(null);

  const { data: detectedCountry } = useQuery({
    enabled: !!user && !activated,
    queryFn: detectCountry,
    queryKey: onboardingKeys.detectedCountry,
    retry: false,
    staleTime: Infinity,
  });

  // Typed values win; until then, what we could work out.
  const values = {
    company,
    // Typed as "City, country"; the detected country is a head start.
    country: country ?? detectedCountry ?? '',
    fullName: fullName ?? nameOf(user?.displayName),
    role,
    source,
  };
  const name = splitName(values.fullName);
  /** A single word typed: the profile needs both halves, so say so rather
   *  than leaving the button mysteriously disabled. */
  const needsLastName = name.first !== '' && name.last === '';
  const complete =
    name.first !== '' &&
    name.last !== '' &&
    values.role !== '' &&
    values.company.trim() !== '' &&
    values.country.trim() !== '';

  const submit = useMutation({
    mutationFn: completeOnboarding,
  });

  /**
   * Done — by submitting here, or by arriving already activated. Same exit as
   * LoginPage: hand the session to the extension and stay on this /login*
   * page, or go where they were headed.
   */
  useEffect(() => {
    if (loading || !user || !activated || finished.current) return;
    finished.current = true;
    void handOffToExtension(user);
    if (!isExtensionFlow) void navigate(redirect, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, user, activated]);

  /**
   * The form actually shown — not `onboarding_completed`'s other half, which
   * only ever counts the people who got through it. Without this, someone
   * sent here on their way to a room and leaving looks like nothing happened.
   */
  const viewed = useRef(false);
  useEffect(() => {
    if (loading || !user || activated || viewed.current) return;
    viewed.current = true;
    trackEvent('onboarding_viewed');
  }, [loading, user, activated]);

  if (loading) {
    return (
      <AuthLayout showcase={<Showcase />}>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-ui-lg text-text-secondary" role="status">
            Setting up your account…
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (!user) {
    return <Navigate replace to={withSignInParams('/login', searchParams)} />;
  }

  if (activated) {
    return isExtensionFlow ? <ExtensionSignedIn /> : null;
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setFormError(null);
    if (!complete || values.role === '') {
      setFormError('Please fill in all required fields');
      return;
    }
    try {
      const result = await submit.mutateAsync({
        company: values.company,
        country: values.country.trim(),
        firstName: name.first,
        lastName: name.last,
        role: values.role,
        // Optional: left out when nothing was picked.
        ...(values.source !== '' ? { source: values.source } : {}),
        // The campaign that brought them, from their first landing.
        utm: readUtm(),
      });
      if (!result.activated) {
        trackEvent('onboarding_failed', { status: 'activation_timeout' });
        setFormError('Activation timeout. Please refresh the page.');
        return;
      }
      trackEvent('onboarding_completed', {
        company: values.company.trim(),
        country: values.country.trim(),
        country_detected: values.country.trim() === detectedCountry,
        role: values.role,
        source: values.source,
      });
      // Everything fetched before this point was refused for want of an
      // account; the session flips `activated`, which runs the exit above.
      await queryClient.invalidateQueries();
      await refreshSession();
    } catch (err) {
      trackEvent('onboarding_failed', {
        status: err instanceof ApiError ? String(err.status) : 'network',
      });
      setFormError(
        err instanceof ApiError
          ? err.message
          : 'Something went wrong. Please try again.',
      );
    }
  };

  return (
    <AuthLayout showcase={<Showcase />}>
      <div className="flex flex-1 flex-col items-center">
        {/* Centred in the column, as designed. `my-auto` rather than
            centring the row: a form taller than the screen then scrolls
            from its top instead of being cut off above it. */}
        <div className="my-auto w-full max-w-sm py-8 duration-500 animate-in fade-in slide-in-from-bottom-2 motion-reduce:animate-none">
          <h1 className="!my-0 !text-auth-title !font-bold !text-text-primary">
            A bit about you.
          </h1>
          <p className="!mt-[10px] text-ui-lg text-text-secondary">
            So your personas know who they&rsquo;re talking to.
          </p>

          <form
            className="mt-[20px] flex flex-col gap-[14px]"
            noValidate
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
          >
            <Field htmlFor="fullName" label="Full name">
              <Input
                autoComplete="name"
                className={CONTROL}
                id="fullName"
                onChange={(e) => setFullName(e.target.value)}
                placeholder="First and last name"
                required
                value={values.fullName}
              />
              {needsLastName ? (
                <p className="!mt-[6px] text-caption text-text-muted">
                  Add your last name too.
                </p>
              ) : null}
            </Field>

            <ChipGroup
              label="Your role"
              onChange={(value) => setRole(value as OnboardingRole)}
              options={ONBOARDING_ROLES}
              value={values.role}
            />

            <div className="grid grid-cols-1 gap-[12px] sm:grid-cols-2">
              <Field htmlFor="company" label="Company or university">
                <Input
                  autoComplete="organization"
                  className={CONTROL}
                  id="company"
                  onChange={(e) => setCompany(e.target.value)}
                  placeholder="Where you work or study"
                  required
                  value={values.company}
                />
              </Field>
              <Field htmlFor="country" label="Where are you based?">
                <Input
                  autoComplete="address-level2"
                  className={CONTROL}
                  id="country"
                  onChange={(e) => setCountry(e.target.value)}
                  placeholder="City, country"
                  required
                  value={values.country}
                />
              </Field>
            </div>

            <ChipGroup
              label="How did you hear about Floto?"
              onChange={(value) => setSource(value as OnboardingSource | '')}
              optional
              options={ONBOARDING_SOURCES}
              value={values.source}
            />

            {formError ? (
              <p
                className="rounded-button bg-tag-critical px-3 py-2 text-ui text-text-primary"
                role="alert"
              >
                {formError}
              </p>
            ) : null}

            <div className="pt-[4px]">
              <Button
                className="h-[48px] w-full cursor-pointer justify-center gap-2 rounded-button bg-brand text-ui-lg font-semibold text-white hover:bg-brand-alt active:scale-[0.98] disabled:cursor-not-allowed"
                disabled={!complete || submit.isPending}
                type="submit"
              >
                {submit.isPending ? (
                  'Setting up…'
                ) : (
                  <>
                    Start using Floto
                    <ArrowRightIcon aria-hidden="true" className="size-4" />
                  </>
                )}
              </Button>
              {submit.isPending ? (
                <p className="!mt-[12px] text-center text-caption text-text-muted">
                  This may take a few moments. Please don&rsquo;t close this
                  page.
                </p>
              ) : (
                // The only way off this page for a new user: every protected
                // route sends them back here. For the wrong Google account, or
                // a sign-up that never got its account and needs a fresh one.
                <p className="!mt-[12px] text-center text-caption text-text-muted">
                  Not {user.email ?? 'you'}?{' '}
                  <button
                    className="cursor-pointer text-room-violet underline-offset-2 hover:underline"
                    onClick={() => {
                      // Signed out, the page's own !user branch takes them
                      // back to /login with the redirect and extension params.
                      void signOut();
                    }}
                    type="button"
                  >
                    Use a different account
                  </button>
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
}

function Field({
  children,
  htmlFor,
  label,
}: {
  children: ReactNode;
  htmlFor: string;
  label: string;
}) {
  return (
    <div className="flex flex-col gap-[6px]">
      <label
        className="text-ui-strong font-semibold text-text-primary"
        htmlFor={htmlFor}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

/**
 * One-of-many as pills rather than a dropdown: every option is visible at
 * once, and choosing is one click. A radio group to assistive tech, with the
 * chosen pill filled violet.
 */
function ChipGroup({
  label,
  onChange,
  optional = false,
  options,
  value,
}: {
  label: string;
  /** Called with '' when an optional group's choice is clicked again. */
  onChange: (value: string) => void;
  /** Says "(optional)" by the label, and lets the choice be taken back. */
  optional?: boolean;
  options: readonly { label: string; value: string }[];
  value: string;
}) {
  return (
    <div className="flex flex-col gap-[6px]">
      <p className="text-ui-strong font-semibold text-text-primary">
        {label}
        {optional ? (
          <span className="font-medium text-text-muted"> (optional)</span>
        ) : null}
      </p>
      <div
        aria-label={label}
        className="flex flex-wrap gap-[6px]"
        role="radiogroup"
      >
        {options.map((option) => {
          const chosen = option.value === value;
          return (
            <button
              aria-checked={chosen}
              className={[
                'h-[34px] cursor-pointer rounded-pill border px-[14px] text-ui font-semibold transition active:scale-[0.98]',
                chosen
                  ? 'border-room-violet bg-room-violet text-white'
                  : 'border-hairline-alt bg-surface text-text-primary hover:border-text-muted',
              ].join(' ')}
              key={option.value}
              onClick={() => onChange(optional && chosen ? '' : option.value)}
              role="radio"
              type="button"
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Showcase() {
  return (
    <ShowcasePanel title="Help us personalize your experience.">
      {/* The yellow shapes peek in from the panel's edges, as in the design. */}
      <span className="absolute top-[57%] bottom-[3%] -left-8 -z-20 w-16 rounded-pill bg-sunny" />
      <span className="absolute top-[7%] bottom-[12%] -right-8 -z-20 w-20 rounded-pill bg-sunny" />

      {GREETERS.map((greeter) => (
        <div className={`absolute ${greeter.place}`} key={greeter.name}>
          <div
            className={`relative animate-drift motion-reduce:animate-none ${greeter.delay}`}
          >
            <PersonaAvatar
              className="ring-4 ring-white shadow-floating"
              persona={{
                accent: 0,
                avatarProfile: greeter.profile,
                id: `onboarding-${greeter.name}`,
                name: greeter.name,
              }}
              size="hero"
            />
            <span
              className={`absolute rounded-button bg-surface px-2.5 py-1 text-ui-strong font-bold text-text-primary shadow-soft ${greeter.hi}`}
            >
              Hi!
            </span>
          </div>
        </div>
      ))}
    </ShowcasePanel>
  );
}
